import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, FileText, Users, Download } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const teasers = [
  {
    id: "verslag-verenigingen",
    icon: FileText,
    eyebrow: "Nieuw · 24 september 2026",
    title: "Verslag van de gesprekken met de verenigingen",
    text:
      "De rode draden uit de verenigingsgesprekken zijn gebundeld in een verslag.",
    tone: "accent",
    updateHref: "/updates#2026-09-verslag-verenigingen",
    pdfHref: "/downloads/verslag-gesprekken-verenigingen.pdf",
  },
  {
    id: "enquete-uitslag",
    icon: BarChart3,
    eyebrow: "Uitslag · 1 augustus 2026",
    title: "Menaam heeft gesproken — 208 reacties",
    text:
      "94% wil behoud, 90% steunt een gedeeld gebouw en 93% zegt: nú aan de slag.",
    tone: "default",
    updateHref: "/enquete-uitslag",
    updateLabel: "Bekijk uitslag",
    pdfHref: "/downloads/enquete-uitslag-menaam.pdf",
  },
  {
    id: "doarpscafe",
    icon: Users,
    eyebrow: "Terugblik · 16 april 2026",
    title: "Wat zegt ús doarp? — Doarpscafé",
    text:
      "De uitkomsten van het Doarpscafé in een overzichtelijke infographic.",
    tone: "default",
    updateHref: "/updates#2026-04-doarpscafe",
    pdfHref: "/downloads/wat-zegt-us-doarp.pdf",
  },
];

function Card({ t, i }) {
  const Icon = t.icon;
  const iconBg =
    t.tone === "accent"
      ? "bg-[#F4A261]/15 text-[#8B3A2A]"
      : "bg-[#2D6A4F]/12 text-[#2D6A4F]";
  return (
    <Reveal delay={i * 0.08}>
      <article
        data-testid={`updates-teaser-${t.id}`}
        className="group h-full flex flex-col rounded-3xl border border-[#2D6A4F]/12 bg-white p-7 transition-all hover:border-[#2D6A4F]/30 hover:-translate-y-0.5 hover:shadow-lg"
      >
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${iconBg} mb-5`}>
          <Icon size={22} />
        </div>
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-[#2D6A4F] mb-2">
          {t.eyebrow}
        </div>
        <h3 className="font-heading text-lg font-bold text-[#1A2E22] mb-2 leading-snug">
          {t.title}
        </h3>
        <p className="text-sm text-[#1A2E22]/65 leading-relaxed m-0 mb-6">{t.text}</p>

        <div className="mt-auto pt-4 border-t border-[#2D6A4F]/10 flex items-center justify-between gap-3">
          <Link
            to={t.updateHref}
            data-testid={`updates-teaser-${t.id}-link`}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#2D6A4F] hover:text-[#245a41] transition-colors"
          >
            {t.updateLabel || "Lees update"}
            <ArrowRight
              size={15}
              className="group-hover:translate-x-0.5 transition-transform"
            />
          </Link>
          <a
            href={t.pdfHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`updates-teaser-${t.id}-pdf`}
            aria-label={`Download PDF — ${t.title}`}
            className="inline-flex items-center gap-1 text-xs font-bold text-[#1A2E22]/60 hover:text-[#2D6A4F] transition-colors"
          >
            <Download size={13} /> PDF
          </a>
        </div>
      </article>
    </Reveal>
  );
}

export default function UpdatesTeaser() {
  return (
    <section
      id="nieuws"
      data-testid="updates-teaser-section"
      className="py-24 sm:py-32 bg-[#FAF7F2]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <Eyebrow testid="updates-teaser-eyebrow">Nieuws & terugblik</Eyebrow>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A2E22] mb-4">
                Wat er tot nu toe<br />is gebeurd
              </h2>
              <p className="text-[#1A2E22]/70 leading-relaxed m-0">
                Alle mijlpalen, uitkomsten en verslagen op één plek. Van het Doarpscafé
                tot de laatste verenigingsgesprekken.
              </p>
            </div>
            <Link
              to="/updates"
              data-testid="updates-teaser-all-link"
              className="group inline-flex items-center gap-2 self-start md:self-end text-sm font-bold text-[#2D6A4F] hover:text-[#245a41] transition-colors whitespace-nowrap"
            >
              Bekijk alle updates
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {teasers.map((t, i) => (
            <Card key={t.id} t={t} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
